import { useEffect, useMemo, useState } from 'react';
import { dataService } from '@/services/dataService';
import { ActivityEvent } from '@/types/entities';
import { useI18n } from '@/i18n';

const ACTOR_TYPES = ['all', 'agent', 'user', 'system'] as const;
type ActorFilter = (typeof ACTOR_TYPES)[number];
const RESULTS = ['all', 'good', 'warn', 'crit', 'info'] as const;
type ResultFilter = (typeof RESULTS)[number];

/**
 * AGENT LOG — a read-only view over the same ActivityEvent repository as
 * the Activity module, now including real diagnostics-run/local-command/
 * automation/memory CRUD events. No token-usage or cost column exists
 * because nothing has ever actually run against a real AI provider — that
 * column shows a fixed honest placeholder instead of a fabricated number.
 */
export function AgentLogPage() {
  const { t } = useI18n();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [actorFilter, setActorFilter] = useState<ActorFilter>('all');
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');

  useEffect(() => {
    dataService.listActivity().then(setEvents);
  }, []);

  const visible = useMemo(() => {
    return [...events]
      .filter((e) => actorFilter === 'all' || e.actorType === actorFilter)
      .filter((e) => resultFilter === 'all' || e.result === resultFilter)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [events, actorFilter, resultFilter]);

  return (
    <div>
      <h1>{t('nav.agentLog')}</h1>
      <p style={{ color: 'var(--text-2)', marginTop: -8, marginBottom: 16 }}>{t('agentLog.subtitle')}</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="text-input" style={{ width: 160 }} value={actorFilter} onChange={(e) => setActorFilter(e.target.value as ActorFilter)}>
          {ACTOR_TYPES.map((a) => <option key={a} value={a}>{t(`agentLog.actor.${a}`)}</option>)}
        </select>
        <select className="text-input" style={{ width: 160 }} value={resultFilter} onChange={(e) => setResultFilter(e.target.value as ResultFilter)}>
          {RESULTS.map((r) => <option key={r} value={r}>{t(`agentLog.result.${r}`)}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 8 }}>
        {visible.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>{t('agentLog.empty')}</div>
        ) : (
          <div className="table-scroll">
            <table className="dt">
              <thead>
                <tr>
                  <th>{t('agentLog.col.when')}</th>
                  <th>{t('agentLog.col.actorType')}</th>
                  <th>{t('agentLog.col.actor')}</th>
                  <th>{t('agentLog.col.action')}</th>
                  <th>{t('agentLog.col.target')}</th>
                  <th>{t('agentLog.col.result')}</th>
                  <th>{t('agentLog.col.usage')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 12 }}>{e.createdAt}</td>
                    <td>{t(`agentLog.actor.${e.actorType}`)}</td>
                    <td>{e.actorLabel}</td>
                    <td>{e.action}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{e.targetEntityType ? `${e.targetEntityType}${e.targetEntityId ? ` — ${e.targetEntityId}` : ''}` : '—'}</td>
                    <td><span className={`badge ${e.result}`}>{t(`agentLog.result.${e.result}`)}</span></td>
                    <td style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('agentLog.usageUnavailable')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
